import { useState } from 'react';
import { founderDashboardAPI } from '../../../../services/api';
import {
  PageHeader,
  PageState,
  StaleNotice,
  StatusBadge,
} from './OutreachShared';
import {
  focusRing,
  formatDateTime,
  humanize,
  listFrom,
  useOutreachResource,
} from './outreachUtils';

const PAGE_SIZE = 25;

export default function OutreachQueuePage() {
  const [offset, setOffset] = useState(0);
  const resource = useOutreachResource(
    () => founderDashboardAPI.getOutreachCandidates({ limit: PAGE_SIZE, offset }),
    [offset]
  );
  const rows = listFrom(resource.data, 'candidates', 'items', 'rows');
  const total = Number(resource.data?.pagination?.total ?? resource.data?.total ?? rows.length);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Queue"
        description="Masked candidate evaluation, scheduling, collisions, and explicit eligibility outcomes."
        refreshing={resource.refreshing}
        onRefresh={resource.reload}
      />
      <StaleNotice
        generatedAt={resource.data?.generatedAt || resource.data?.generated_at}
        loadedAt={resource.loadedAt}
      />

      <PageState
        loading={resource.loading}
        error={resource.error}
        permissionDenied={resource.permissionDenied}
        empty={rows.length === 0}
        emptyTitle="Queue is empty"
        emptyMessage="No masked candidates are currently waiting or recently evaluated."
        onRetry={resource.reload}
      >
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-[1050px] w-full text-left text-sm">
              <caption className="sr-only">
                Lifecycle outreach candidate queue with masked identities and safety reasons
              </caption>
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Masked identity</th>
                  <th className="px-4 py-3">Current state</th>
                  <th className="px-4 py-3">State entered</th>
                  <th className="px-4 py-3">Cohort</th>
                  <th className="px-4 py-3">Next eligible</th>
                  <th className="px-4 py-3">Collision</th>
                  <th className="px-4 py-3">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, index) => {
                  const status =
                    row.status ||
                    row.queueStatus ||
                    row.queue_status ||
                    row.decision ||
                    row.evidence?.deliveryStatus ||
                    (row.anomaly ? 'blocked' : 'pending');
                  const reason =
                    row.reasonCode ||
                    row.reason_code ||
                    row.skipReason ||
                    row.skip_reason ||
                    row.cancelReason ||
                    row.cancel_reason ||
                    row.evidence?.reasonCode;
                  return (
                    <tr key={row.id || row.candidateId || `${offset}-${index}`} className="align-top">
                      <td className="px-4 py-4"><StatusBadge value={status} /></td>
                      <td className="px-4 py-4 font-medium text-gray-900">
                        {row.maskedIdentity ||
                          row.masked_identity ||
                          row.recipientMasked ||
                          row.identity ||
                          'Masked'}
                      </td>
                      <td className="px-4 py-4 text-gray-700">
                        {humanize(
                          row.currentState ||
                            row.current_state ||
                            row.state ||
                            row.stateKey
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-gray-600">
                        {formatDateTime(row.stateEnteredAt || row.state_entered_at)}
                      </td>
                      <td className="px-4 py-4 text-gray-700">
                        {humanize(
                          row.cohort ||
                            row.cohortAssignment ||
                            row.cohort_assignment ||
                            'not assigned'
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-gray-600">
                        {row.nextEligibleAt || row.next_eligible_at
                          ? formatDateTime(row.nextEligibleAt || row.next_eligible_at)
                          : 'Not scheduled'}
                      </td>
                      <td className="px-4 py-4 text-gray-700">
                        {row.collision
                          ? humanize(
                              row.collision.code ||
                                row.collision.reason ||
                                row.collision
                            )
                          : humanize(row.collisionReason || row.collision_reason || 'none')}
                      </td>
                      <td className="max-w-xs px-4 py-4 text-gray-700">
                        {reason ? humanize(reason) : 'Eligible'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500">
            Showing {offset + 1}–{Math.min(offset + rows.length, total)} of {total}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
              className={`min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${focusRing}`}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={offset + rows.length >= total}
              onClick={() => setOffset((value) => value + PAGE_SIZE)}
              className={`min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${focusRing}`}
            >
              Next
            </button>
          </div>
        </div>
      </PageState>
    </div>
  );
}
